import { createContext } from 'react';
import { ICampaign } from 'src/lib/schemas';
import { ILayer } from 'src/lib/schemas/account/template/TemplateSchema';
import { ICreatomateTemplate } from 'src/models/creatomate-template/interfaces';
import { IIndustry } from 'src/services/industries';

export interface ILayerAction {
	id?: string;
	arguments?: any;
}

export interface ILayerSpec {
	name?: string;
	actions?: ILayerAction[];
}

export interface IVariation {
	id?: string;
	title?: string;
	isLoading?: boolean;
	layeredFile?: string;
	layerSpecs?: ILayerSpec[];
	template?: any;
}

interface IPalette {
	strategy: {
		id: string;
	};
	options: Array<{
		name: string;
		colors: string[];
	}>;
}

export type Kind = 'Product' | 'Service';

interface ITemplateAttribute {
	mainIndustry?: string;
	industries?: string[];
	style?: string;
	season?: string;
	thumbnail?: string;
	palettes?: IPalette;
	fonts?: string[];
	tags?: string[];
	priority?: number;
	kind?: Kind;
}

export interface IPrompt {
	name: string;
	description: string;
}

export interface ITemplate {
	name?: string;
	id: string;
	enabled?: boolean;
	attributes?: ITemplateAttribute;
	variations?: IVariation[];
	scope?: 'platform' | 'account';
	score?: number;
	prompts?: IPrompt[];
}

interface IAccountInfo {
	folderName: string;
	existFolder: boolean;
}

interface IImageAttributes {
	dimensions: {
		width: number;
		height: number;
	};
	layeredFile: string;
	flatFile: string;
	layers: Array<any>;
}

export interface IUpdatePayload {
	variationId: string;
	attributes: {
		image: IImageAttributes;
	};
}

export interface IPayloadsByCase {
	idCase: number;
	payloadList: IUpdatePayload[];
}

interface ITemplateContext {
	platformTemplates: ITemplate[];
	accountTemplates: ITemplate[];
	creatomateTemplates: ICreatomateTemplate[];
	styles: string[];
	seasons: string[];
	industries: IIndustry[];
	isOptionsReady: boolean;
	selectedIndustry: IIndustry | null;
	setSelectedIndustry: React.Dispatch<React.SetStateAction<IIndustry | null>>;
	industriesLoaded: boolean;
	fetchIndustries: () => Promise<void>;
	selectedTemplatesData: (ITemplate | ICreatomateTemplate)[];
	setSelectedTemplatesData: React.Dispatch<
		React.SetStateAction<(ITemplate | ICreatomateTemplate)[]>
	>;
	setIndustries: React.Dispatch<React.SetStateAction<IIndustry[]>>;
	setIndustriesLoaded: React.Dispatch<React.SetStateAction<boolean>>;
	selectedTemplate: ITemplate | null;
	setSelectedTemplate: (template: ITemplate | null) => void;
	fetchTemplates: (campaign?: ICampaign) => Promise<void>;
	selectedTemplateIds: string[];
	isSelectTemplateModalOpen: boolean;
	setSelectTemplateModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
	templatesLoaded: boolean;
	setTemplatesLoaded: React.Dispatch<React.SetStateAction<boolean>>;
	setSelectedTemplateIds: React.Dispatch<React.SetStateAction<string[]>>;
	payloads: IUpdatePayload[];
	setPayloads: React.Dispatch<React.SetStateAction<IUpdatePayload[]>>;
	payloadsByCase: IPayloadsByCase[];
	handleSetPayloadsByCase: (
		caseId: number,
		payload: IUpdatePayload,
		isRegenerate: boolean,
	) => void;
	setPayloadsByCaseState: React.Dispatch<
		React.SetStateAction<IPayloadsByCase[]>
	>;
	resetSelectedTemplate: () => void;
	layers: ILayer[];
	setLayers: React.Dispatch<React.SetStateAction<ILayer[]>>;
	loadingLayers: boolean;
	configuredLayers: { variationId: string; layerSpec: ILayerSpec[] }[];
	setConfiguredLayers: React.Dispatch<
		React.SetStateAction<{ variationId: string; layerSpec: ILayerSpec[] }[]>
	>;
	styleValues: string[];
	seasonsValues: string[];
	setStyleValues: React.Dispatch<React.SetStateAction<string[]>>;
	setSeasonValues: React.Dispatch<React.SetStateAction<string[]>>;
	customTemplates: ICreatomateTemplate[];
	setCustomTemplates: React.Dispatch<
		React.SetStateAction<ICreatomateTemplate[]>
	>;
	filteredCustomTemplates: ICreatomateTemplate[];
	setFilteredCustomTemplates: React.Dispatch<
		React.SetStateAction<ICreatomateTemplate[]>
	>;
	customAccountTemplates: ICreatomateTemplate[];
	setCustomAccountTemplates: React.Dispatch<
		React.SetStateAction<ICreatomateTemplate[]>
	>;
	processedCustomTemplates: ICreatomateTemplate[];
	setProcessedCustomTemplates: React.Dispatch<
		React.SetStateAction<ICreatomateTemplate[]>
	>;
	layeredTemplates: any[];
	setLayeredTemplates: React.Dispatch<React.SetStateAction<any[]>>;
	lastSearchWithCampaign: boolean | null;
}

const TemplateContext = createContext<ITemplateContext>({
	platformTemplates: [],
	accountTemplates: [],
	creatomateTemplates: [],
	styles: [],
	seasons: [],
	industries: [],
	isOptionsReady: false,
	selectedIndustry: null,
	setSelectedIndustry: () => {},
	industriesLoaded: false,
	fetchIndustries: async () => {},
	selectedTemplatesData: [],
	setSelectedTemplatesData: () => {},
	setIndustries: () => {},
	setIndustriesLoaded: () => {},
	selectedTemplate: null,
	setSelectedTemplate: () => {},
	fetchTemplates: async () => {},
	selectedTemplateIds: [],
	isSelectTemplateModalOpen: false,
	setSelectTemplateModalOpen: () => {},
	templatesLoaded: false,
	setTemplatesLoaded: () => {},
	setSelectedTemplateIds: () => {},
	payloads: [],
	setPayloads: () => {},
	payloadsByCase: [],
	handleSetPayloadsByCase: () => {},
	setPayloadsByCaseState: () => {},
	resetSelectedTemplate: () => {},
	layers: [],
	setLayers: () => {},
	loadingLayers: false,
	configuredLayers: [],
	setConfiguredLayers: () => {},
	styleValues: [],
	seasonsValues: [],
	setStyleValues: () => {},
	setSeasonValues: () => {},
	customTemplates: [],
	setCustomTemplates: () => {},
	filteredCustomTemplates: [],
	setFilteredCustomTemplates: () => {},
	customAccountTemplates: [],
	setCustomAccountTemplates: () => {},
	processedCustomTemplates: [],
	setProcessedCustomTemplates: () => {},
	layeredTemplates: [],
	setLayeredTemplates: () => {},
	lastSearchWithCampaign: null,
});

export default TemplateContext;
